<script lang="ts" setup>
import { ref, onMounted, nextTick, onUnmounted, watch } from "vue";

import * as echarts from "echarts/core";
import { BarChart } from "echarts/charts";
import type { EChartsType } from "echarts/core";

import { GridComponent, LegendComponent, TooltipComponent } from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
import { UniversalTransition } from "echarts/features";

import { getConfig, getColor, getMaxValueLength } from "@/features/Charts/config/echarts";
import { useThemeChange } from "@/features/Charts/composables/useThemeChange";
import { getBarEndRadius, getBarRadii } from "@/features/Charts/utils/barBorderRadius";
import { buildChartTooltipHtml } from "@/features/Charts/utils/chartTooltip";
import { prefersReducedMotion, seriesMotion } from "@/features/Charts/utils/chartMotion";
import { shouldHandOverRows, zeroedSeries, type DrawnSeries } from "@/features/Charts/utils/rowsOutHandover";
import { useTooltip } from "@stores/useTooltip";

import { verticalBarChartProps } from "../props/echartProps";

echarts.use([BarChart, GridComponent, UniversalTransition, SVGRenderer, LegendComponent, TooltipComponent]);

const props = defineProps({
  ...verticalBarChartProps,
});

const categories = ref<string[]>([]);

let chartInstance: EChartsType | null = null;
let resizeObserver: ResizeObserver | null = null;

const containerRef = ref<HTMLElement | null>(null);
const chartRef = ref<HTMLElement | null>(null);
const config = ref();

// What the previous draw put on screen, so a navigation can animate those columns out before the
// next ones arrive. Must match chartMotion's rows-out duration: ECharts takes an update's duration
// from the option already in place, not from the draw that starts it. See utils/rowsOutHandover.ts.
const COLUMNS_OUT_MS = 150;
// Container size at the last resize, so an observer callback that reports no change does nothing.
const lastResizedTo = { w: 0, h: 0 };
let lastDrawnCategories: string[] = [];
let lastDrawnSeries: DrawnSeries[] = [];
let columnsOutTimer: ReturnType<typeof setTimeout> | null = null;

// Shared cursor-following tooltip (RTLCA-4942) — replaces the per-chart native ECharts tooltip.
const tooltip = useTooltip();

// ECharts item colour can be a flat string or a gradient descriptor; pull a usable colour for the swatch.
const seriesColor = (params: any): string | undefined =>
  typeof params?.color === "string" ? params.color : params?.color?.colorStops?.[0]?.color;

function preamble() {
  // Get config
  config.value = getConfig("VerticalBarChart", props.settings, props.data);

  // Register theme
  echarts.registerTheme(`${config.value.name}Theme`, config.value.theme);

  // Get categories
  categories.value = Object.values(props.data)[0]?.entries.map((entry) => entry.label) ?? [];
}

// Builds the option from the current props. Split out of onMounted so the chart can be redrawn
// when its data is swapped in place — the dashboard does that without remounting the chart
// (previewing a revision refetches outside the skeleton's loading gate).
function updateChart(drawOptions?: { afterColumnsOut?: boolean }) {
  if (!chartInstance) return;

  preamble();

  // Columns out, then columns in: the set being replaced shrinks back to the axis first, and the
  // real draw follows once it is gone.
  if (
    !drawOptions?.afterColumnsOut &&
    shouldHandOverRows({
      settings: props.settings,
      reducedMotion: prefersReducedMotion(),
      drawnCategories: lastDrawnCategories,
      drawnSeries: lastDrawnSeries,
      nextCategories: categories.value,
    })
  ) {
    if (columnsOutTimer) clearTimeout(columnsOutTimer);
    chartInstance.setOption({ series: zeroedSeries(lastDrawnSeries) });
    columnsOutTimer = setTimeout(() => {
      columnsOutTimer = null;
      updateChart({ afterColumnsOut: true });
    }, COLUMNS_OUT_MS);
    return;
  }

  const maxLength = getMaxValueLength(props.data);

  // Corner radii per column. The config's fixed [4, 4, 0, 0] rounds the top, which is the end a
  // *positive* column grows towards — on a negative one (module D on the stage chart) it rounds the
  // end sitting on the zero line and squares off the free end at the bottom. This follows the sign.
  const seriesValues = Object.values(props.data).map((item) =>
    categories.value.map((_, categoryIndex) => item.entries[categoryIndex]?.value ?? 0)
  );
  const barRadii = getBarRadii(seriesValues, categories.value.length, {
    orientation: "vertical",
    stacked: props.settings.stacked,
  });

  const option = {
    grid: {
      ...config.value.grid,
    },
    legend: {
      show: props.settings.legend,
    },
    yAxis: {
      ...config.value.yAxis,
      name: props.unit,
      nameGap: Math.max(maxLength * 9, 50),
    },
    xAxis: {
      ...config.value.xAxis,
      data: categories.value,
      inverse: props.settings.inverse,
      axisLabel: {
        ...config.value.xAxis.axisLabel,
        rotate: props.settings.rotateLabels ? props.settings.rotateLabels : 0,
      },
    },
    series: Object.entries(props.data).map(([key, item], index) => ({
      ...config.value.series,
      // A stable identity per series, so a redraw UPDATES it rather than replacing it: a replaced
      // series is destroyed, and a destroyed column gets no leave animation.
      id: `${key}`,
      // One timing for arriving, leaving and moving columns (opt-in via settings.motion).
      ...seriesMotion(props.settings, { reducedMotion: prefersReducedMotion() }),
      // barMinHeight is applied per segment, so in a stack it inflates a small segment past its real
      // length and gives a zero-valued one a stub that lands on its neighbour (RTLCA-5189).
      ...(props.settings.stacked ? { barMinHeight: 0 } : {}),
      realtimeSort: props.settings.realtimeSort,
      name: item.name ?? key,
      data: item.entries.map((entry, categoryIndex) => ({
        value: entry.value,
        id: entry.id,
        itemStyle: {
          borderRadius: barRadii[index]?.[categoryIndex] ?? getBarEndRadius(entry.value, "vertical"),
          ...(props.settings.legend
            ? {}
            : {
                color: getColor({
                  index,
                  value: entry.value,
                  series: props.data,
                }),
              }),
        },
        ...props.eventMetadata(),
      })),
      stack: props.settings.stacked ? "stack" : undefined,
      cursor: props.onClick ? "pointer" : "default",
    })),
    // Native ECharts tooltip disabled — the shared cursor-following tooltip is driven from the
    // mouseover/mouseout handlers below (RTLCA-4942).
    tooltip: { show: false },
  };

  // replaceMerge rather than notMerge: both drop a series the redraw no longer sends (which is what
  // notMerge was here for), but notMerge rebuilds every series from scratch, so nothing could ever
  // animate out and every draw replayed a full grow-in. The category axis is replaced either way.
  chartInstance.setOption(option, { replaceMerge: ["series"] });

  // What is on screen now, which is what the next navigation animates out.
  lastDrawnCategories = [...categories.value];
  lastDrawnSeries = Object.entries(props.data).map(([key, item]) => ({
    id: `${key}`,
    itemIds: item.entries.map((entry) => entry.id),
  }));
}

// Split out of onMounted so a theme flip can dispose the instance and recreate it (picking up a
// freshly registered theme) through the same path, rather than duplicating the event wiring.
function wireChartEvents() {
  if (!chartInstance) return;

  chartInstance.on("mouseover", (params: any) => {
    if (params.seriesType !== "bar") return;
    const value = Number(params.value?.value ?? params.value);
    tooltip.showTooltip(
      buildChartTooltipHtml({
        label: String(params.name ?? ""),
        value: Number.isFinite(value) ? value : 0,
        unit: props.unit,
        color: seriesColor(params),
      })
    );
    if (props.onHover) props.onHover(params);
  });

  chartInstance.on("mouseout", (params: any) => {
    tooltip.hideTooltip();
    if (params.seriesType === "bar" && props.onLeave) {
      props.onLeave();
    }
  });

  chartInstance.on("globalout", () => tooltip.hideTooltip());

  chartInstance.on("click", (params) => {
    if (props.onClick) {
      props.onClick(params);
    }
  });
}

function createChartInstance() {
  if (!chartRef.value) return;
  chartInstance = echarts.init(chartRef.value, `${config.value.name}Theme`);
  // The size ECharts just measured is the baseline the observer compares against. Seeded from
  // zero it treated its own first callback as a change and resized mid-animation, which lands on
  // the last frame — the chart's first draw snapped into place.
  lastResizedTo.w = chartRef.value.clientWidth;
  lastResizedTo.h = chartRef.value.clientHeight;

  updateChart();
  wireChartEvents();
}

onMounted(() => {
  preamble();
  createChartInstance();

  if (chartRef.value) {
    resizeObserver = new ResizeObserver(async () => {
      await nextTick();
      if (!chartRef.value) return;
      // Only when the box really changed. A ResizeObserver fires once the moment it is registered,
      // and resizing makes zrender relayout and repaint immediately, which lands on the LAST frame
      // of the animation in flight — so the chart's first draw snapped into place instead of
      // growing in. The same trap as the resize that used to follow every setOption here.
      const { clientWidth, clientHeight } = chartRef.value;
      if (clientWidth === lastResizedTo.w && clientHeight === lastResizedTo.h) return;
      lastResizedTo.w = clientWidth;
      lastResizedTo.h = clientHeight;
      chartInstance?.resize();
    });

    resizeObserver.observe(document.body!);
  }
});

watch(
  [() => props.data, () => props.unit],
  async () => {
    await nextTick();
    updateChart();
  },
  { deep: true }
);

onUnmounted(() => {
  if (columnsOutTimer) clearTimeout(columnsOutTimer);
  tooltip.hideTooltip();
  resizeObserver?.disconnect();
  chartInstance!.dispose();
});

useThemeChange(() => {
  if (!chartRef.value) return;
  chartInstance?.dispose();
  chartInstance = null;
  preamble();
  createChartInstance();
});
</script>

<template>
  <div
    ref="containerRef"
    class="relative h-full w-full">
    <div class="relative h-full min-h-0 flex-1">
      <div
        ref="chartRef"
        class="absolute inset-0" />
    </div>
  </div>
</template>
